<template>
  <div class="sec-shell">
    <header class="sec-header">
      <div>
        <div class="eyebrow">Account</div>
        <h1>Security</h1>
        <p class="subtitle">Two-factor authentication adds a 6-digit code from an authenticator app on top of your password.</p>
      </div>
    </header>

    <!-- ==================== Status card ==================== -->
    <section
      class="sec-status"
      :class="{
        'sec-status-on': status !== 'loading' && enabled,
        'sec-status-off': status !== 'loading' && !enabled,
        'sec-status-loading': status === 'loading',
      }"
    >
      <div class="status-left">
        <div class="status-icon" :class="enabled ? 'icon-on' : 'icon-off'">
          <svg v-if="enabled" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M9 12l2 2 4-4" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
          <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </div>
        <div class="status-copy">
          <div class="status-title">
            Two-Factor Authentication
            <span
              class="status-pill"
              :class="{
                'pill-on': status !== 'loading' && enabled,
                'pill-off': status !== 'loading' && !enabled,
                'pill-loading': status === 'loading',
              }"
            >{{ statusPillText }}</span>
          </div>
          <div class="status-desc">
            <span v-if="status === 'loading'">Loading…</span>
            <span v-else-if="enabled">
              Your account is protected with an authenticator code.
              <span v-if="enrolledAt" class="status-since">Enabled {{ enrolledAt }}.</span>
            </span>
            <span v-else>Default is off — enable it when you're ready.</span>
          </div>
        </div>
      </div>
      <div class="status-right">
        <button
          v-if="!enabled && status !== 'enrolling' && status !== 'loading'"
          @click="beginEnroll"
          class="btn btn-primary"
          :disabled="isSubmitting"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon">
            <path d="M12 5v14M5 12h14" stroke-linecap="round" />
          </svg>
          Enable 2FA
        </button>
        <button
          v-else-if="enabled"
          @click="beginDisable"
          class="btn btn-danger-ghost"
          :disabled="isSubmitting"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" class="btn-icon">
            <path d="M18 6L6 18M6 6l12 12" stroke-linecap="round" />
          </svg>
          Disable 2FA
        </button>
      </div>
    </section>

    <!-- ==================== Enrollment flow ==================== -->
    <transition name="fade">
      <section v-if="enrollData" class="sec-section">
        <div class="section-head">
          <div class="section-title">
            <span class="section-badge section-badge-info">STEP</span>
            Set up your authenticator app
          </div>
          <div class="section-sub">Google Authenticator, Authy, 1Password — any TOTP app works.</div>
        </div>

        <ol class="sec-steps">
          <li><span class="step-num">1</span> Open your authenticator app.</li>
          <li><span class="step-num">2</span> Scan the QR code, or paste the secret manually.</li>
          <li><span class="step-num">3</span> Enter the current 6-digit code below to finish enrollment.</li>
        </ol>

        <div class="enroll-grid">
          <div class="qr-frame">
            <img :src="enrollData.qrDataUrl" alt="2FA QR code" class="qr-image" />
          </div>
          <div class="enroll-info">
            <div class="info-label">Manual entry secret</div>
            <div class="info-mono">{{ enrollData.secret }}</div>
            <div class="info-hint">SHA1 · 6 digits · 30-second period · Google-Authenticator-compatible.</div>
          </div>
        </div>

        <div class="code-row">
          <div class="code-field">
            <label>6-digit code</label>
            <input
              v-model.trim="enrollCode"
              maxlength="6"
              inputmode="numeric"
              placeholder="••••••"
              class="code-input"
            />
          </div>
          <div class="code-actions">
            <button
              @click="cancelEnroll"
              type="button"
              class="btn btn-ghost"
              :disabled="isSubmitting"
            >Cancel</button>
            <button
              @click="confirmEnroll"
              type="button"
              class="btn btn-success"
              :disabled="enrollCode.length !== 6 || isSubmitting"
            >
              <svg v-if="!isSubmitting" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" class="btn-icon">
                <path d="M5 13l4 4L19 7" stroke-linecap="round" stroke-linejoin="round" />
              </svg>
              <span v-else class="btn-spinner"></span>
              {{ isSubmitting ? 'Verifying…' : 'Confirm & Enable' }}
            </button>
          </div>
        </div>
      </section>
    </transition>

    <!-- ==================== Disable flow ==================== -->
    <transition name="fade">
      <section v-if="disableMode" class="sec-section sec-section-danger">
        <div class="section-head">
          <div class="section-title section-title-danger">
            <span class="section-badge section-badge-danger">DISABLE</span>
            Disable Two-Factor Authentication
          </div>
          <div class="section-sub">
            Enter your current 6-digit code to confirm. After this, only your password will be required to log in.
          </div>
        </div>

        <div class="code-row">
          <div class="code-field">
            <label>6-digit code</label>
            <input
              v-model.trim="disableCode"
              maxlength="6"
              inputmode="numeric"
              placeholder="••••••"
              class="code-input"
            />
          </div>
          <div class="code-actions">
            <button
              @click="disableMode = false; disableCode = ''"
              type="button"
              class="btn btn-ghost"
              :disabled="isSubmitting"
            >Cancel</button>
            <button
              @click="confirmDisable"
              type="button"
              class="btn btn-danger"
              :disabled="disableCode.length !== 6 || isSubmitting"
            >
              <span v-if="isSubmitting" class="btn-spinner"></span>
              {{ isSubmitting ? 'Disabling…' : 'Disable 2FA' }}
            </button>
          </div>
        </div>
      </section>
    </transition>

    <!-- ==================== Callouts ==================== -->
    <transition name="fade">
      <div v-if="error" class="callout callout-error">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 8v4M12 16h.01" stroke-linecap="round" />
        </svg>
        <span>{{ error }}</span>
      </div>
    </transition>
    <transition name="fade">
      <div v-if="success" class="callout callout-success">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M22 11.08V12a10 10 0 11-5.93-9.14" stroke-linecap="round" stroke-linejoin="round" />
          <path d="M22 4L12 14.01l-3-3" stroke-linecap="round" stroke-linejoin="round" />
        </svg>
        <span>{{ success }}</span>
      </div>
    </transition>
  </div>
</template>

<script>
import http from "@/http-common.js";

export default {
  name: "AccountSecurity",
  data() {
    return {
      status: "loading",     // 'loading' | 'idle' | 'enrolling' | 'disabling'
      enabled: false,
      enrolledAt: null,
      enrollData: null,      // { secret, otpauthUrl, qrDataUrl }
      enrollCode: "",
      disableMode: false,
      disableCode: "",
      isSubmitting: false,
      error: null,
      success: null,
    };
  },
  computed: {
    statusPillText() {
      if (this.status === "loading") return "Loading";
      return this.enabled ? "Enabled" : "Off";
    },
  },
  mounted() {
    this.refreshStatus();
  },
  methods: {
    clearMessages() { this.error = null; this.success = null; },
    async refreshStatus() {
      this.clearMessages();
      this.status = "loading";
      try {
        const r = await http.get("/auth/totp/status");
        this.enabled = !!(r.data && r.data.data && r.data.data.enabled);
        this.enrolledAt = (r.data && r.data.data && r.data.data.enrolledAt) || null;
        this.status = "idle";
      } catch (e) {
        this.error = "Failed to load 2FA status.";
        this.status = "idle";
      }
    },
    async beginEnroll() {
      this.clearMessages();
      this.isSubmitting = true;
      try {
        const r = await http.post("/auth/totp/setup");
        this.enrollData = r.data && r.data.data ? r.data.data : null;
        this.enrollCode = "";
        this.status = "enrolling";
      } catch (e) {
        this.error = e.response?.data?.message || "Failed to start enrollment.";
      } finally {
        this.isSubmitting = false;
      }
    },
    async confirmEnroll() {
      this.clearMessages();
      if (!/^\d{6}$/.test(this.enrollCode)) {
        this.error = "Enter the 6-digit code from your authenticator app.";
        return;
      }
      this.isSubmitting = true;
      try {
        await http.post("/auth/totp/enable", { secret: this.enrollData.secret, code: this.enrollCode });
        this.success = "2FA enabled. From your next login you'll be asked for a 6-digit code.";
        this.enrollData = null;
        this.enrollCode = "";
        await this.refreshStatus();
      } catch (e) {
        this.error = e.response?.data?.message || "Could not verify the code. Try again with a fresh code.";
      } finally {
        this.isSubmitting = false;
      }
    },
    cancelEnroll() {
      this.enrollData = null;
      this.enrollCode = "";
      this.status = "idle";
      this.clearMessages();
    },
    beginDisable() {
      this.clearMessages();
      this.disableMode = true;
      this.disableCode = "";
    },
    async confirmDisable() {
      this.clearMessages();
      if (!/^\d{6}$/.test(this.disableCode)) {
        this.error = "Enter the 6-digit code from your authenticator app.";
        return;
      }
      this.isSubmitting = true;
      try {
        await http.post("/auth/totp/disable", { code: this.disableCode });
        this.success = "2FA disabled.";
        this.disableMode = false;
        this.disableCode = "";
        await this.refreshStatus();
      } catch (e) {
        this.error = e.response?.data?.message || "Could not disable 2FA. Check the code and try again.";
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<style scoped>
/* ==================== Shell ==================== */
.sec-shell {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 20px;
  background: linear-gradient(180deg, #fbfaf7 0%, #f4f0e5 100%);
  overflow-y: auto;
}

/* ==================== Header ==================== */
.sec-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.eyebrow {
  font-size: 11px;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #1B2A47;
  font-weight: 600;
  margin-bottom: 4px;
}
.sec-header h1 {
  font-size: 28px;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.1;
  margin: 0;
}
.subtitle {
  font-size: 13px;
  color: #64748b;
  margin: 4px 0 0;
  max-width: 640px;
}

/* ==================== Status card ==================== */
.sec-status {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 18px 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  transition: border-color 0.18s, background 0.18s;
}
.sec-status-on { border-color: #a7f3d0; background: linear-gradient(180deg, #fff, #f0fdf4); }
.sec-status-off { border-color: #e2e8f0; }
.sec-status-loading { opacity: 0.85; }

.status-left {
  display: flex;
  align-items: center;
  gap: 14px;
  min-width: 0;
  flex: 1;
}
.status-icon {
  width: 44px; height: 44px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.status-icon svg { width: 22px; height: 22px; }
.icon-on  { background: #d1fae5; color: #065f46; }
.icon-off { background: #f1f5f9; color: #64748b; }

.status-copy { min-width: 0; }
.status-title {
  font-size: 15px;
  font-weight: 700;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.status-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}
.pill-on { background: #d1fae5; color: #065f46; }
.pill-off { background: #f1f5f9; color: #64748b; }
.pill-loading { background: #eef2f7; color: #1B2A47; }

.status-desc {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}
.status-since {
  color: #94a3b8;
  margin-left: 4px;
}
.status-right { flex-shrink: 0; }

/* ==================== Section (enroll / disable) ==================== */
.sec-section {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 22px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.sec-section-danger {
  border-color: #fecaca;
  background: linear-gradient(180deg, #fff, #fef2f2);
}
.section-head {}
.section-title {
  font-size: 14px;
  font-weight: 700;
  color: #0f172a;
  display: inline-flex;
  align-items: center;
  gap: 10px;
}
.section-title-danger { color: #991b1b; }
.section-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
}
.section-badge-info { background: #eef2f7; color: #1B2A47; }
.section-badge-danger { background: #fee2e2; color: #b91c1c; }
.section-sub {
  font-size: 12px;
  color: #64748b;
  margin-top: 4px;
}

/* Numbered step list */
.sec-steps {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.sec-steps li {
  font-size: 13px;
  color: #475569;
  display: flex;
  align-items: center;
  gap: 10px;
}
.step-num {
  width: 22px; height: 22px;
  border-radius: 999px;
  background: #eef2f7;
  color: #1B2A47;
  font-size: 11px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

/* Enroll grid — QR + info side by side */
.enroll-grid {
  display: grid;
  grid-template-columns: 200px 1fr;
  gap: 22px;
  align-items: center;
}
@media (max-width: 640px) {
  .enroll-grid { grid-template-columns: 1fr; }
}
.qr-frame {
  background: #fff;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  display: inline-block;
  box-shadow: 0 4px 12px -6px rgba(15, 23, 42, 0.08);
}
.qr-image { display: block; width: 176px; height: 176px; }

.enroll-info { min-width: 0; }
.info-label {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #475569;
  margin-bottom: 6px;
}
.info-mono {
  background: #0f172a;
  color: #94a3b8;
  padding: 10px 12px;
  border-radius: 10px;
  font-family: 'SFMono-Regular', Menlo, monospace;
  font-size: 12px;
  word-break: break-all;
  line-height: 1.5;
}
.info-hint {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 8px;
}

/* Code input row */
.code-row {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  padding-top: 6px;
  border-top: 1px solid #f1f5f9;
}
.code-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.code-field label {
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.code-input {
  width: 190px;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  background: #f8fafc;
  outline: none;
  font-family: 'SFMono-Regular', Menlo, monospace;
  font-size: 20px;
  font-weight: 700;
  letter-spacing: 0.35em;
  color: #0f172a;
  text-align: center;
  transition: border-color 0.15s, background 0.15s, box-shadow 0.15s;
}
.code-input::placeholder {
  color: #cbd5e1;
  letter-spacing: 0.35em;
}
.code-input:focus {
  border-color: #1B2A47;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(27, 42, 71, 0.14);
}
.code-actions { display: inline-flex; gap: 8px; }

/* ==================== Buttons ==================== */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 18px;
  border-radius: 10px;
  font-weight: 600;
  font-size: 13px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: all 0.15s;
  outline: none;
}
.btn:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-primary {
  background: linear-gradient(135deg, #1B2A47 0%, #2C3E63 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(27, 42, 71, 0.5);
}
.btn-primary:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(27, 42, 71, 0.55);
}
.btn-success {
  background: linear-gradient(135deg, #059669 0%, #047857 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(5, 150, 105, 0.5);
}
.btn-success:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(5, 150, 105, 0.55);
}
.btn-danger {
  background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
  color: #fff;
  box-shadow: 0 6px 16px -6px rgba(220, 38, 38, 0.5);
}
.btn-danger:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 10px 24px -8px rgba(220, 38, 38, 0.55);
}
.btn-danger-ghost {
  background: #fff;
  color: #dc2626;
  border-color: #fecaca;
}
.btn-danger-ghost:hover:not(:disabled) {
  background: #fef2f2;
  border-color: #f87171;
}
.btn-ghost {
  background: #fff;
  color: #475569;
  border-color: #e2e8f0;
}
.btn-ghost:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
}
.btn-icon { width: 15px; height: 15px; }
.btn-spinner {
  width: 14px; height: 14px;
  border-radius: 999px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  animation: spin 0.7s linear infinite;
}
@keyframes spin { to { transform: rotate(360deg); } }

/* ==================== Callouts (error / success) ==================== */
.callout {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  border-radius: 12px;
  font-size: 13px;
  font-weight: 500;
  border: 1px solid transparent;
}
.callout svg { width: 18px; height: 18px; flex-shrink: 0; margin-top: 1px; }
.callout-error {
  background: #fef2f2;
  border-color: #fecaca;
  color: #991b1b;
}
.callout-success {
  background: #ecfdf5;
  border-color: #a7f3d0;
  color: #065f46;
}

/* ==================== Transitions ==================== */
.fade-enter-active, .fade-leave-active { transition: all 0.22s cubic-bezier(0.4, 0, 0.2, 1); }
.fade-enter-from, .fade-leave-to { opacity: 0; transform: translateY(-4px); }
</style>
